import { randomItemOrThrow } from "@indietabletop/appkit";
import { Lookup } from "../oss/lookup.ts";
import type {
  DiceRoll,
  DiceType,
  Effect,
  Ref,
  RichText,
  RollResults,
  WeaponDamage,
  WeaponRef,
} from "./common.ts";
import { NameGenerator, type NameTriple } from "./NameGenerator.ts";
import { RepairLocation } from "./RepairLocation.ts";
import { Treatment } from "./Treatment.ts";
import { Weapon } from "./Weapon.ts";

export function randomItemFromSelectOn<T extends { selectOn: number[] }>(
  items: T[],
) {
  const distributed = items.flatMap((git) => git.selectOn.map(() => git));
  return randomItemOrThrow(distributed);
}

export type NameGeneratorData = {
  id: string;
  name: string;
  names: NameTriple[];
  joinPattern: string;
};

export type GitProfile = {
  id: string;
  name: string;
  cost: number;
  selectOn: RollResults;
  scoot: DiceRoll | DiceRoll[];
  headbutt: DiceType;
  toughness: number;
  canDrive: boolean;
  rules?: Ref[];
};

export type GitSpecialRule = {
  id: string;
  name: string;
  description: RichText;
  isCampaignRule?: boolean;
};

export type VehicleSpecialRule = {
  id: string;
  name: string;
  description: RichText;
};

export type VehicleWeaponPlacement = "FIXED" | "PINTLE_MOUNTED";

export type VehicleWeaponRef = WeaponRef & {
  placement: VehicleWeaponPlacement;
};

export type VehicleProfile = {
  id: string;
  name: string;
  cost: number;
  engine: DiceType;
  toughness: number;
  seats: number;
  selectOn: RollResults;
  mods: Ref[];
  rules: Ref[];
};

export type WeaponProfile = {
  id: string;
  name: string;
  cost: number;
  hands: number;
  range: number;
  dice: DiceRoll;
  damage: WeaponDamage;
  rules: Ref[];
  selectOnGit: RollResults;
  selectOnVehicle: RollResults;
};

export type WeaponSpecialRule = {
  id: string;
  name: string;
  description: RichText;
};

export type TreatmentProfile = {
  id: string;
  selectOn: RollResults;
} & (
  | { name: string; description: RichText; effects: Effect[]; fromGear?: false }
  | { fromGear: true; description?: RichText }
);

export type TreatmentLocationProfile = {
  id: string;
  name: string;
  costPublic: number;
  costPrivate: number;
  selectOn: RollResults;
  treatments: TreatmentProfile[];
  isChiral?: boolean;
};

export type RepairProfile = {
  id: string;
  selectOn: RollResults;
} & (
  | { name: string; description: string; effects: Effect[]; fromMod?: false }
  | { fromMod: true }
);

export type RepairLocationProfile = {
  id: string;
  name: string;
  costBodgeJob: number;
  costProperJob: number;
  selectOn: RollResults;
  repairs: RepairProfile[];
};

export type GearProfile = {
  id: string;
  name: string;
  cost: number;
  description: RichText;
  selectOn: RollResults;
  effects?: Effect[];
};

export type ModProfile = {
  id: string;
  name: string;
  cost: number;
  description: RichText;
  selectOn: RollResults;
  effects?: Effect[];
};

export type FactionProfile = {
  id: string;
  name: string;
  fluff: string;
  rules: GitSpecialRule[];
};

export type RulesetConstants = {
  defaultCarryCapacity: number;
  defaultAttitude: number;
  maxAttitude: number;
  chefsRecMaxModelsCost: number;
  aLaCarteCapsBudget: number;
  chaosModeCostThreshold: number;
  downtroddenItemCostThreshold: number;
  campaignModeCostMultiplier: number;
};

export type RulesetData = {
  version: string;
  gits: GitProfile[];
  gitSpecialRules: GitSpecialRule[];
  vehicleSpecialRules: VehicleSpecialRule[];
  vehicles: VehicleProfile[];
  weapons: WeaponProfile[];
  weaponSpecialRules: WeaponSpecialRule[];
  gear: GearProfile[];
  mods: ModProfile[];
  treatments: TreatmentLocationProfile[];
  repairs: RepairLocationProfile[];
  factions: FactionProfile[];
  constants: RulesetConstants;
  generators: NameGeneratorData[];
};

export class TreatmentLocation {
  ruleset: Ruleset;
  data: TreatmentLocationProfile;

  constructor(ruleset: Ruleset, data: TreatmentLocationProfile) {
    this.ruleset = ruleset;
    this.data = data;
  }

  get id() {
    return this.data.id;
  }

  get cost() {
    return this.data.costPrivate;
  }

  get name() {
    return this.data.name;
  }

  get treatments() {
    return Lookup.fromItems(
      this.data.treatments.map((data) => new Treatment(this, data)),
    );
  }

  get selectOn() {
    return this.data.selectOn;
  }

  getRandomTreatment() {
    return randomItemFromSelectOn(this.treatments.toArray());
  }
}

function halve<T>(array: T[]): [T[], T[]] {
  const mid = Math.ceil(array.length / 2);
  return [array.slice(0, mid), array.slice(mid)];
}

export class Ruleset {
  private data: RulesetData;

  constructor(data: RulesetData) {
    this.data = data;
  }

  get constants() {
    return this.data.constants;
  }

  get version() {
    return this.data.version;
  }

  get gits() {
    return Lookup.fromItems(this.data.gits);
  }

  get vehicles() {
    return Lookup.fromItems(this.data.vehicles);
  }

  get weapons() {
    return Lookup.fromItems(
      this.data.weapons.map((data) => new Weapon(this, data)),
    );
  }

  get diceTypeToDamage() {
    const map: Record<DiceType, WeaponDamage> = {
      D4: { weak: 0, big: 1 },
      D6: { weak: 1, big: 2 },
      D8: { weak: 2, big: 3 },
      D10: { weak: 3, big: 5 },
      D12: { weak: 5, big: 10 },
    };

    return map;
  }

  get gitSpecialRules() {
    return Lookup.fromItems(this.data.gitSpecialRules);
  }

  get vehicleSpecialRules() {
    return Lookup.fromItems(this.data.vehicleSpecialRules);
  }

  get weaponSpecialRules() {
    return Lookup.fromItems(this.data.weaponSpecialRules);
  }

  get gear() {
    return Lookup.fromItems(this.data.gear);
  }

  get treatmentLocations() {
    return Lookup.fromItems(
      this.data.treatments
        .flatMap((profile) => {
          const [selectOnLeft, selectOnRight] = halve(profile.selectOn);

          return profile.isChiral
            ? [
                {
                  ...profile,
                  id: `left_${profile.id}`,
                  name: `Left ${profile.name}`,
                  selectOn: selectOnLeft,
                },
                {
                  ...profile,
                  id: `right_${profile.id}`,
                  name: `Right ${profile.name}`,
                  selectOn: selectOnRight,
                },
              ]
            : profile;
        })
        .map((data) => new TreatmentLocation(this, data)),
    );
  }

  get mods() {
    return Lookup.fromItems(this.data.mods);
  }

  get repairLocations() {
    return Lookup.fromItems(
      this.data.repairs.map((data) => new RepairLocation(this, data)),
    );
  }

  get factions() {
    return Lookup.fromItems(this.data.factions);
  }

  get nameGenerators() {
    return Lookup.fromItems(
      this.data.generators.map((data) => new NameGenerator(data)),
    );
  }

  get gangNameGenerator() {
    return this.nameGenerators.get("gang").valueOrThrow();
  }

  get orcNameGenerator() {
    return this.nameGenerators.get("git").valueOrThrow();
  }

  get gnawballNameGenerator() {
    return this.nameGenerators.get("gnawball").valueOrThrow();
  }

  getRandomModelType() {
    const item = randomItemFromSelectOn([
      ...this.data.gits.map((gitType) => {
        return { type: "GIT" as const, selectOn: gitType.selectOn, gitType };
      }),

      // Apart from the random gits, the rules specify that on 11 and 12 you
      // can randomly generate a vehicle or pick a git. This option takes
      // us that path, but currently we are omitting the 'select a git' option,
      // as that would mean that random generation is no longer synchronous,
      // which complicates things a lot.
      { type: "CHOOSE_VEHICLE" as const, selectOn: [11, 12] },
    ]);

    // If we got the choose vehicle option, generate a random vehicle type.
    if (item.type === "CHOOSE_VEHICLE") {
      const vehicleType = this.getRandomVehicleType();
      return { type: "VEHICLE" as const, vehicleType };
    }

    // Otherwise just return the git.
    return item;
  }

  getRandomGitType() {
    return randomItemFromSelectOn(this.data.gits);
  }

  getRandomWeapon() {
    return randomItemOrThrow(this.data.weapons);
  }

  getRandomTreatmentLocation() {
    return randomItemFromSelectOn(this.treatmentLocations.toArray());
  }

  getRandomGitLoot() {
    return randomItemFromSelectOn([
      ...this.data.gear.map((gear) => {
        return {
          type: "GEAR" as const,
          selectOn: gear.selectOn,
          item: gear,
        };
      }),

      ...this.data.weapons.map((weapon) => {
        return {
          type: "WEAPON" as const,
          selectOn: weapon.selectOnGit,
          item: weapon,
        };
      }),
    ]);
  }

  getRandomGear() {
    return randomItemOrThrow(this.data.gear);
  }

  getRandomVehicleType() {
    return randomItemFromSelectOn(this.data.vehicles);
  }

  getRandomMod() {
    return randomItemOrThrow(this.data.mods);
  }

  getRandomRepairLocation() {
    return randomItemFromSelectOn(this.repairLocations.toArray());
  }

  getRandomVehicleLoot() {
    return randomItemFromSelectOn([
      ...this.data.mods.map((item) => {
        return {
          type: "MOD" as const,
          selectOn: item.selectOn,
          item,
        };
      }),

      ...this.data.weapons.map((item) => {
        return {
          type: "WEAPON" as const,
          selectOn: item.selectOnVehicle,
          item,
        };
      }),
    ]);
  }

  getRandomFaction() {
    return randomItemOrThrow(this.data.factions);
  }
}
